import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    getDigitalTwinByTrackingNumber,
} from "../../../api/digitalTwinService";

export default function AdminDigitalTwinPage() {
    const navigate = useNavigate();

    const [trackingNumber, setTrackingNumber] = useState("");
    const [twin, setTwin] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [showRaw, setShowRaw] = useState(false);

    const loadTwin = async () => {
        if (!trackingNumber.trim()) {
            setError("Enter tracking number.");
            return;
        }

        setLoading(true);
        setError("");
        setTwin(null);

        try {
            const data = await getDigitalTwinByTrackingNumber(trackingNumber.trim());
            setTwin(data);
        } catch (err) {
            console.error(err);
            setError(
                err?.response?.data?.message ||
                err?.message ||
                "Digital Twin not found or server error."
            );
        } finally {
            setLoading(false);
        }
    };

    const latestTelemetry = useMemo(() => {
        const list = twin?.telemetryRecords || twin?.telemetries || [];
        return [...list].sort(
            (a, b) => new Date(b.recordedAt || b.createdAt || 0) - new Date(a.recordedAt || a.createdAt || 0)
        )[0];
    }, [twin]);

    const latestSecurity = useMemo(() => {
        const list = twin?.securityStatuses || [];
        return [...list].sort(
            (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
        )[0];
    }, [twin]);

    const trustScore = Number(twin?.trustScore ?? twin?.riskScore ?? 0);
    const latitude = twin?.latitude ?? latestTelemetry?.latitude;
    const longitude = twin?.longitude ?? latestTelemetry?.longitude;

    return (
        <div className="w-full">
            <div className="mb-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900">
                        Digital Twin Control Center
                    </h1>
                    <p className="text-slate-600 mt-2">
                        Search, inspect and monitor shipment digital twins with telemetry,
                        security state and AI risk indicators.
                    </p>
                </div>

                <div className="flex flex-wrap gap-3">
                    <button
                        type="button"
                        onClick={() => navigate("/admin/iot-map")}
                        className="rounded-xl bg-indigo-600 px-5 py-3 text-white font-semibold hover:bg-indigo-700 transition"
                    >
                        Open IoT Map
                    </button>

                    <button
                        type="button"
                        onClick={() => navigate("/admin/iot-devices")}
                        className="rounded-xl bg-slate-800 px-5 py-3 text-white font-semibold hover:bg-slate-900 transition"
                    >
                        IoT Devices
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow border border-slate-200 p-6 mb-6">
                <div className="flex flex-col md:flex-row gap-3">
                    <input
                        value={trackingNumber}
                        onChange={(e) => setTrackingNumber(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") loadTwin();
                        }}
                        placeholder="Enter tracking number"
                        className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600"
                    />

                    <button
                        type="button"
                        onClick={loadTwin}
                        disabled={loading}
                        className="rounded-xl bg-blue-600 px-5 py-3 text-white font-semibold hover:bg-blue-700 transition disabled:opacity-60"
                    >
                        {loading ? "Loading..." : "Load Digital Twin"}
                    </button>

                    {twin && (
                        <button
                            type="button"
                            onClick={loadTwin}
                            className="rounded-xl bg-slate-700 px-5 py-3 text-white font-semibold hover:bg-slate-800 transition"
                        >
                            Refresh
                        </button>
                    )}
                </div>

                {error && (
                    <div className="mt-4 rounded-xl border border-red-300 bg-red-50 p-4 text-red-700">
                        {error}
                    </div>
                )}
            </div>

            {!twin && !loading && (
                <div className="bg-white rounded-2xl shadow border border-slate-200 p-10 text-center">
                    <div className="text-5xl mb-4">??</div>
                    <h2 className="text-2xl font-bold">No Digital Twin Loaded</h2>
                    <p className="text-slate-500 mt-2">
                        Enter a tracking number to load the shipment digital twin.
                    </p>
                </div>
            )}

            {twin && (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-5 mb-6">
                        <StatCard
                            title="Tracking Number"
                            value={twin.trackingNumber || trackingNumber}
                            subtitle="Shipment identifier"
                            color="blue"
                        />

                        <StatCard
                            title="Shipment Status"
                            value={twin.currentStatus || twin.status || twin.shipmentStatus || "�"}
                            subtitle="Current shipment state"
                            color="slate"
                        />

                        <StatCard
                            title="Trust Score"
                            value={`${twin.trustScore ?? "�"}%`}
                            subtitle="Adaptive trust engine"
                            color={trustScore >= 80 ? "green" : trustScore >= 60 ? "yellow" : "red"}
                        />

                        <StatCard
                            title="Risk Level"
                            value={twin.riskLevel ?? "�"}
                            subtitle="AI security risk"
                            color={String(twin.riskLevel).toLowerCase() === "low" ? "green" : "yellow"}
                        />

                        <StatCard
                            title="Blockchain"
                            value={twin.blockchainVerified ? "Verified" : latestSecurity?.blockchainStatus || "Pending"}
                            subtitle="Audit integrity"
                            color={twin.blockchainVerified ? "green" : "yellow"}
                        />
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6">
                        <div className="xl:col-span-2 bg-white rounded-2xl shadow border border-slate-200 p-6">
                            <h2 className="text-2xl font-bold mb-5">
                                Shipment Twin Summary
                            </h2>

                            <div className="grid md:grid-cols-2 gap-4 text-sm">
                                <InfoRow label="Twin ID" value={twin.id ?? "�"} />
                                <InfoRow label="Shipment ID" value={twin.shipmentId ?? twin.shipment?.id ?? "�"} />
                                <InfoRow label="Tracking Number" value={twin.trackingNumber || trackingNumber || "�"} />
                                <InfoRow label="Current Status" value={twin.currentStatus || twin.status || "�"} />
                                <InfoRow label="Risk Level" value={twin.riskLevel ?? "�"} />
                                <InfoRow label="Anomaly" value={twin.hasAnomaly ? "Yes" : "No"} />
                                <InfoRow label="Requires Inspection" value={twin.requiresInspection ? "Yes" : "No"} />
                                <InfoRow
                                    label="Last Updated"
                                    value={
                                        twin.lastUpdatedAt
                                            ? new Date(twin.lastUpdatedAt).toLocaleString()
                                            : "�"
                                    }
                                />
                            </div>

                            <div className="mt-6">
                                <div className="flex justify-between mb-2">
                                    <span className="font-semibold">Trust Score</span>
                                    <span className="font-bold">{twin.trustScore ?? 0}%</span>
                                </div>

                                <div className="h-4 w-full rounded-full bg-slate-200 overflow-hidden">
                                    <div
                                        className={`h-full ${trustScore >= 80
                                                ? "bg-green-500"
                                                : trustScore >= 60
                                                    ? "bg-yellow-500"
                                                    : "bg-red-500"
                                            }`}
                                        style={{
                                            width: `${Math.min(Number(twin.trustScore ?? 0), 100)}%`,
                                        }}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl shadow border border-slate-200 p-6">
                            <h2 className="text-2xl font-bold mb-5">
                                Location & IoT
                            </h2>

                            <div className="space-y-3">
                                <InfoRow label="Latitude" value={latitude ?? "�"} />
                                <InfoRow label="Longitude" value={longitude ?? "�"} />
                                <InfoRow label="IoT Status" value={latestTelemetry?.iotStatus || twin.iotStatus || "�"} />
                                <InfoRow label="Battery" value={formatValue(twin.batteryLevel, "%")} />
                                <InfoRow label="Signal" value={formatValue(twin.signalQuality, "%")} />
                            </div>

                            {latitude && longitude && (
                                <button
                                    type="button"
                                    onClick={() => navigate("/admin/iot-map")}
                                    className="mt-5 w-full rounded-xl bg-indigo-600 px-5 py-3 text-white font-semibold hover:bg-indigo-700 transition"
                                >
                                    Open Location Map
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
                        <DataCard title="Latest Telemetry">
                            {latestTelemetry ? (
                                <div className="grid md:grid-cols-2 gap-4 text-sm">
                                    <InfoRow label="Latitude" value={latestTelemetry.latitude ?? "�"} />
                                    <InfoRow label="Longitude" value={latestTelemetry.longitude ?? "�"} />
                                    <InfoRow label="IoT Status" value={latestTelemetry.iotStatus || "�"} />
                                    <InfoRow
                                        label="Recorded At"
                                        value={
                                            latestTelemetry.recordedAt
                                                ? new Date(latestTelemetry.recordedAt).toLocaleString()
                                                : "�"
                                        }
                                    />
                                </div>
                            ) : (
                                <EmptyText text="No telemetry records available." />
                            )}
                        </DataCard>

                        <DataCard title="Latest Security Status">
                            {latestSecurity ? (
                                <div className="grid md:grid-cols-2 gap-4 text-sm">
                                    <InfoRow label="GeoFence" value={latestSecurity.geoFenceStatus || "�"} />
                                    <InfoRow label="Blockchain" value={latestSecurity.blockchainStatus || "�"} />
                                    <InfoRow label="Route" value={latestSecurity.routeStatus || "�"} />
                                    <InfoRow label="ETA" value={latestSecurity.etaStatus || "�"} />
                                    <InfoRow
                                        label="Created At"
                                        value={
                                            latestSecurity.createdAt
                                                ? new Date(latestSecurity.createdAt).toLocaleString()
                                                : "�"
                                        }
                                    />
                                </div>
                            ) : (
                                <EmptyText text="No security records available." />
                            )}
                        </DataCard>
                    </div>

                    <DataCard title="Threat History">
                        {twin.threatHistoryRecords?.length > 0 ? (
                            <ResponsiveTable
                                columns={["Detected At", "Threat Type", "Risk Score", "Message"]}
                                rows={twin.threatHistoryRecords.map((t) => [
                                    t.detectedAt ? new Date(t.detectedAt).toLocaleString() : "�",
                                    t.threatType || "�",
                                    t.riskScore ?? "�",
                                    t.message || "�",
                                ])}
                            />
                        ) : (
                            <EmptyText text="No threat history records available." />
                        )}
                    </DataCard>

                    <div className="mt-6">
                        <DataCard title="Developer Information">
                            <button
                                type="button"
                                onClick={() => setShowRaw((v) => !v)}
                                className="rounded-xl bg-slate-800 px-5 py-3 text-white font-semibold hover:bg-slate-900 transition"
                            >
                                {showRaw ? "Hide Raw JSON" : "Show Raw JSON"}
                            </button>

                            {showRaw && (
                                <pre className="mt-5 max-h-[420px] overflow-auto rounded-xl bg-slate-950 p-5 text-sm text-green-400">
                                    {JSON.stringify(twin, null, 2)}
                                </pre>
                            )}
                        </DataCard>
                    </div>
                </>
            )}
        </div>
    );
}

function formatValue(value, suffix) {
    if (value === null || value === undefined) return "�";
    return `${value} ${suffix}`;
}

function StatCard({ title, value, subtitle, color = "blue" }) {
    const colors = {
        blue: "bg-blue-50 text-blue-700",
        green: "bg-green-50 text-green-700",
        yellow: "bg-yellow-50 text-yellow-700",
        red: "bg-red-50 text-red-700",
        slate: "bg-slate-50 text-slate-700",
    };

    return (
        <div className="bg-white rounded-2xl shadow border border-slate-200 p-6 hover:shadow-xl transition">
            <p className="text-slate-500">{title}</p>
            <div className={`inline-flex mt-3 px-3 py-1 rounded-xl font-black text-xl ${colors[color]}`}>
                {value ?? "�"}
            </div>
            <p className="text-slate-500 text-sm mt-3">{subtitle}</p>
        </div>
    );
}

function InfoRow({ label, value }) {
    return (
        <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
            <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                {label}
            </p>
            <p className="font-bold text-slate-900 mt-1 break-words">
                {value ?? "�"}
            </p>
        </div>
    );
}

function DataCard({ title, children }) {
    return (
        <div className="bg-white rounded-2xl shadow border border-slate-200 p-6">
            <h2 className="text-2xl font-bold mb-5">{title}</h2>
            {children}
        </div>
    );
}

function EmptyText({ text }) {
    return <p className="text-slate-500">{text}</p>;
}

function ResponsiveTable({ columns, rows }) {
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead className="bg-slate-100">
                    <tr>
                        {columns.map((c) => (
                            <th key={c} className="text-left px-4 py-3">
                                {c}
                            </th>
                        ))}
                    </tr>
                </thead>

                <tbody>
                    {rows.map((row, index) => (
                        <tr key={index} className="border-t hover:bg-slate-50">
                            {row.map((cell, i) => (
                                <td key={i} className="px-4 py-3">
                                    {cell}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}